import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("order_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        if (attempts >= MAX_POLLS) { setState("error"); return; }
      }
      if (attempts >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="bg-paper min-h-screen flex items-center justify-center px-5 pt-[140px] pb-24" data-testid="payment-success-page">
      <div className="bg-white border border-line max-w-md w-full p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={44} className="text-gold animate-spin mx-auto" />
            <h1 className="font-display text-3xl tracking-[0.02em] mt-6">CONFIRMING…</h1>
            <p className="text-navy/60 text-sm mt-2">Hang tight while we confirm your payment.</p>
          </>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={52} className="text-green-600 mx-auto" />
            <p className="text-xs tracking-[0.3em] text-gold font-semibold mt-6">ORDER CONFIRMED</p>
            <h1 className="font-display text-4xl tracking-[0.02em] mt-2">THANK YOU{order?.customer_name ? `, ${order.customer_name.split(" ")[0].toUpperCase()}` : ""}!</h1>
            <p className="text-navy/70 text-sm mt-4">
              Your order is in. We've emailed your receipt{order?.amount_total ? ` for ${money(order.amount_total)}` : ""} and we'll reach out about pickup or delivery.
            </p>
            <Link to="/spirit-store" data-testid="back-to-store" className="inline-flex items-center gap-2 mt-8 bg-ink text-[#f2f0ea] px-8 py-3.5 text-xs font-bold tracking-[0.16em] hover:bg-navy transition-colors">
              KEEP SHOPPING <ArrowRight size={15} />
            </Link>
          </div>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={44} className="text-navy/40 mx-auto" />
            <h1 className="font-display text-3xl tracking-[0.02em] mt-6">ALMOST THERE</h1>
            <p className="text-navy/60 text-sm mt-2">Your payment is still processing. You'll get a confirmation email once it clears.</p>
            <Link to="/spirit-store" className="inline-block mt-8 text-navy underline text-sm">Back to the store</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={44} className="text-red-500 mx-auto" />
            <h1 className="font-display text-3xl tracking-[0.02em] mt-6">SOMETHING WENT WRONG</h1>
            <p className="text-navy/60 text-sm mt-2">We couldn't confirm this order. If you were charged, please contact us and we'll sort it out right away.</p>
            <Link to="/spirit-store" className="inline-block mt-8 bg-ink text-[#f2f0ea] px-8 py-3.5 text-xs font-bold tracking-[0.16em] hover:bg-navy transition-colors">BACK TO STORE</Link>
          </>
        )}
      </div>
    </div>
  );
}
